import type { DrawdownMatrixCell, DrawdownMatrixRow } from '@/types/drawdown';

export interface MatrixTotals {
  amountDue: number;
  amountPaid: number;
  outstanding: number;
  overdue: number;
  overdueInvestorCount: number;
}

/** What an investor still owes past a due date, across all their calls. */
function overdueAmount(row: DrawdownMatrixRow): number {
  return Object.values(row.cells)
    .filter((cell) => cell.days_overdue > 0)
    .reduce((total, cell) => total + cell.amount_due - cell.amount_paid, 0);
}

/** An exempted call is charged to nobody, so it never counts as called. */
function sumCalled(cells: DrawdownMatrixCell[]): {
  amountDue: number;
  amountPaid: number;
} {
  return cells
    .filter((cell) => cell.status !== 'exempted')
    .reduce(
      (totals, cell) => ({
        amountDue: totals.amountDue + cell.amount_due,
        amountPaid: totals.amountPaid + cell.amount_paid,
      }),
      { amountDue: 0, amountPaid: 0 }
    );
}

export function totalColumn(
  rows: DrawdownMatrixRow[],
  drawdownId: string
): { amountDue: number; amountPaid: number } {
  return sumCalled(
    rows.map((row) => row.cells[drawdownId]).filter((cell) => !!cell)
  );
}

export function totalRows(rows: DrawdownMatrixRow[]): MatrixTotals {
  const overdueRows = rows.filter((row) => overdueAmount(row) > 0);

  return rows.reduce<MatrixTotals>(
    (totals, row) => ({
      ...totals,
      amountDue: totals.amountDue + row.amount_due,
      amountPaid: totals.amountPaid + row.amount_paid,
      outstanding: totals.outstanding + row.outstanding,
      overdue: totals.overdue + Math.max(overdueAmount(row), 0),
    }),
    {
      amountDue: 0,
      amountPaid: 0,
      outstanding: 0,
      overdue: 0,
      overdueInvestorCount: overdueRows.length,
    }
  );
}
